import { QueryClient } from '@tanstack/react-query';
import { Divider, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { getSession, useSession } from 'next-auth/react';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import GroupSvg from '../../../../assets/icons/GroupSvg';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import PlaySvg from '../../../../assets/icons/PlaySvg';
import PremiumIconFilled from '../../../../assets/icons/PremiumIconFilled';
import ProfileChecked from '../../../../assets/icons/ProfileChecked';
import ShareSvg from '../../../../assets/icons/ShareSvg';
import coverImage from '../../../../assets/images/app/emptyTemplateCoverPhoto.png';
import theme from '../../../../assets/theme';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import PlanTypeLabel from '../../../../components/_atoms/PlanTypeLabel';
import BaseModal from '../../../../components/_molecules/BaseModal';
import { GeneratorCard } from '../../../../components/_molecules/GeneratorCard';
import { NewLandingPageFooter } from '../../../../components/_molecules/LandingPageFooter';
import ProgressBar from '../../../../components/_molecules/ProgressBar';
import ShareGlobalLesson from '../../../../components/_molecules/ShareGlobalLesson';
import { PremiumLabel } from '../../../../components/_organisms/DesignerTabPane/GeneratorsTab/GeneratorsTab';
import { ConsumerGenerator } from '../../../../components/_organisms/DesignerTabPane/GeneratorsTab/utils';
import GeneratorForm, {
  GeneratorFormState,
  useGeneratorFormState,
} from '../../../../components/_organisms/DesignerTabPane/PromptsTab/GeneratorForm';
import NewAppLayout from '../../../../components/layouts/NewAppLayout';
import { ProfileCircle } from '../../../../components/ProfileCircle';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useConsumeGeneratorAsNewPresentation from '../../../../hooks/useConsumeGeneratorAsNewPresentation';
import { useGeneratorCoverPhoto } from '../../../../hooks/useGeneratorCoverPhoto';
import { useIsDesktop } from '../../../../hooks/useIsDesktop';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRoutePrefetch from '../../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import useSSRGeneratorNew from '../../../../hooks/useSSRGeneratorNew';
import { useTemplateCreatorPhoto } from '../../../../hooks/useTemplateCreatorPhoto';
import backendService from '../../../../services/backendService';
import { PromptInput } from '../../../../services/backendService/types';
import CoreAPIService from '../../../../services/CoreAPIService';
import { getDiscoverCreatorUrl } from '../../../../services/DiscoverService/discoverUtils';
import { generatorFormStateToUserInputFormData } from '../../../../utils/aiGeneratorUtils';
import { getBaseUrl } from '../../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';
import { provisionFlags } from '../../../../utils/ServerSideFeatureFlagUtils';

const NewProfileSection = ({ generator }: { generator: ConsumerGenerator }) => {
  const { thumbnail: creatorThumbnail } = useTemplateCreatorPhoto({
    creatorThumbnailId: generator.creatorThumbnailId,
    isPrivate: false,
  });

  return (
    <ProfileSectionContainer>
      <Link
        passHref
        href={getDiscoverCreatorUrl(generator.creatorName, generator.creatorId)}
      >
        <a>
          <ProfileCircle $radius={40} $showHoverAnimation={true}>
            {creatorThumbnail && (
              <img
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                src={creatorThumbnail}
                height={'10px'}
                width={'100%'}
                alt={`Profile picture of ${generator.creatorName}`}
              />
            )}
            {!creatorThumbnail && (
              <Image
                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                src={coverImage}
                height={'100%'}
                width={'100%'}
                alt={`Profile picture of ${generator.creatorName}`}
              />
            )}
          </ProfileCircle>
        </a>
      </Link>
      <div>
        <Link
          passHref
          href={getDiscoverCreatorUrl(generator.creatorName, generator.creatorId)}
        >
          <a>
            <h2>
              {generator.creatorName}
              <span>
                <Tooltip title={'Verified'}>
                  <IconWrapper
                    style={{ marginLeft: 5 }}
                    height="16px"
                    width="16px"
                    color={'#ffa55b'}
                  >
                    <ProfileChecked />
                  </IconWrapper>
                </Tooltip>
              </span>
            </h2>
          </a>
        </Link>
        <p>Updated {dayjs(generator.modifiedAt).fromNow(true)} ago</p>
      </div>
    </ProfileSectionContainer>
  );
};

const NewGeneratorPreview = ({ authenticated }: { authenticated: boolean }) => {
  const { router } = useRouterMatch();

  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  const isDesktop = useBreakPoint(800);
  const [showShareModal, setShowShareModal] = useState(false);
  const { isEnterprise, isPremium } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  return (
    <GeneratorPreviewContainer>
      {generator && (
        <GeneratorInfoContainer>
          <NewMainContainerWithGeneratorForm
            authenticated={authenticated}
            generator={generator}
          />
          <div style={{ maxWidth: 'max-content' }}>
            {generator.status !== 'published' && <Label>{generator.status}</Label>}
            {generator.aiContent?.isPremiumGenerator && (
              <PlanTypeLabel
                isPremium={isPremium}
                isEnterprise={isEnterprise}
                generatorPlan={generator.aiContent?.generatorPlan}
                isPremiumGenerator={generator.aiContent?.isPremiumGenerator}
              />
            )}
            <h1 style={{ maxWidth: 'max-content' }}>{generator.title} - generator</h1>
            <NewProfileSection generator={generator} />
            <BaseModal
              visible={showShareModal}
              onCancel={() => setShowShareModal(false)}
              title={'Share this generator'}
            >
              <ShareGlobalLesson
                templateId={generator.id}
                isGeneratedByAI={false}
                isGenerator
                title={generator.title || ''}
              />
            </BaseModal>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip mouseEnterDelay={1} title="Share this generator">
                <IconWrapper
                  height="25px"
                  width="25px"
                  onClick={e => {
                    setShowShareModal(true);
                    e.stopPropagation();
                  }}
                  style={{ textDecoration: 'underline', gap: 5 }}
                >
                  <ShareSvg /> Share
                </IconWrapper>
              </Tooltip>
            </div>
            <div style={{ width: 'fit-content' }}>
              {isDesktop && <Divider />}
              <GeneratorStatsContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <IconWrapper
                      height="14px"
                      width="14px"
                      color="#828282"
                      style={{ gap: 5, color: '#828282', cursor: 'default' }}
                    >
                      <MagicWandSvg /> Generations
                    </IconWrapper>
                    <span
                      aria-label="generations"
                      style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                    >
                      {generator?.downloadCount || '0'}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <IconWrapper
                      height="16px"
                      width="16px"
                      color="#828282"
                      style={{ gap: 5, color: '#828282', cursor: 'default' }}
                    >
                      <PlaySvg /> Plays
                    </IconWrapper>
                    <span
                      aria-label="plays"
                      style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                    >
                      {generator?.playCount || '0'}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <IconWrapper
                      height="20px"
                      width="20px"
                      color="#828282"
                      style={{
                        gap: 5,
                        display: 'flex',
                        cursor: 'default',
                        color: '#828282',
                      }}
                    >
                      <GroupSvg /> Students
                    </IconWrapper>
                    <span
                      aria-label="Students"
                      style={{ fontWeight: 'bold', fontSize: isDesktop ? 24 : 18 }}
                    >
                      {generator?.participantCount || '0'}
                    </span>
                  </div>
                </div>
              </GeneratorStatsContainer>

              {isDesktop && <Divider />}
            </div>
            <h2>Generator description</h2>
            <p
              style={{
                width: '100%',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxWidth: 400,
              }}
            >
              {generator.description}
            </p>
          </div>
        </GeneratorInfoContainer>
      )}
    </GeneratorPreviewContainer>
  );
};

const GeneratorPreviewContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 60px;
  position: relative;
`;

const GeneratorInfoContainer = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  grid-template-columns: minmax(280px, 500px) auto;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: start;
  }
  @media (max-width: 800px) {
    align-items: center;
  }
`;

const GeneratorStatsContainer = styled.div`
  width: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 14px;
  padding: 0px 10px;
`;

const Label = styled.div`
  background: ${theme.colors.orange};
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 14px;
  padding: 2px 10px;
  width: fit-content;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
`;

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const NewMainContainerWithGeneratorForm = ({
  generator,
  authenticated,
}: {
  generator: ConsumerGenerator;
  authenticated: boolean;
}) => {
  const { tenantId, router } = useRouterMatch();

  const isDesktop = useIsDesktop();

  const {
    consumeGenerator,
    isError,
    isLoading,
    data,
  } = useConsumeGeneratorAsNewPresentation(tenantId);

  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generator.id,
  );

  const handleConsumeGenerator = useCallback(
    (formState: GeneratorFormState) => {
      if (!authenticated) {
        showAuthGuard();
        return;
      }

      const userInputFormData = generatorFormStateToUserInputFormData(
        formState,
        generator?.aiContent?.promptInputs || [],
      );
      consumeGenerator({
        generatorId: generator.id || '',
        userInputFormData,
        generatorName: generator.title || '',
        onSuccessFunction: template => {
          // redirect to the designer on desktop
          if (isDesktop) {
            localStorage.setItem(
              '@curipod_v1_recentGeneratorConsume',
              JSON.stringify({
                generatorId: generator.id,
                formState,
              }),
            );
            router.push(
              `/${tenantId}/lessons/${template.id}/edit?generatorId=${generator.id}`,
            );
          } else {
            router.push(`/${tenantId}/lessons/${template.id}/edit`);
          }
        },
      });
    },
    [
      authenticated,
      consumeGenerator,
      generator?.aiContent?.promptInputs,
      generator.id,
      generator.title,
      isDesktop,
      router,
      showAuthGuard,
      tenantId,
    ],
  );

  return (
    <>
      {authGuardComponent}
      <GeneratorFormContainer>
        {(isLoading || data) && (
          <LoaderContainer>
            <ProgressBar isSmallTemplates />
          </LoaderContainer>
        )}
        {!(isLoading || data) && (
          <GeneratorFormBackground>
            <div
              style={{
                display: 'grid',
                width: '100%',
                gridTemplateColumns: '1fr',
                padding: '10px 3px',
              }}
            >
              <GeneratorCard
                subTitle=""
                coverImageId={generator?.coverImageId}
                title={generator.title || ''}
              />
            </div>
            <GeneratorsForm
              promptInputs={generator?.aiContent?.promptInputs || []}
              handleConsumeGenerator={handleConsumeGenerator}
              isError={isError}
              promptInputSuggestions={generator?.aiContent?.promptInputSuggestions || []}
              isLoading={isLoading}
              isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
              generatorPlan={generator?.aiContent?.generatorPlan}
              generatorId={generator?.id}
            />
          </GeneratorFormBackground>
        )}
      </GeneratorFormContainer>
    </>
  );
};

const GeneratorFormBackground = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11),
    0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13);
  max-width: 340px;
  position: sticky;
  top: 70px;

  @media (min-width: 500px) {
    min-width: 340px;
  }
`;

const LoaderContainer = styled(GeneratorFormBackground)`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  padding: 20px 5px;
  overflow: hidden;
`;

const GeneratorFormContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    justify-content: start;
  }
`;

const GeneratorsForm = ({
  promptInputs,
  isLoading,
  isError,
  handleConsumeGenerator,
  promptInputSuggestions,
  formState: existingFormState,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
}: {
  promptInputs: PromptInput[];
  isLoading?: boolean;
  isError?: boolean;
  handleConsumeGenerator: (formState: GeneratorFormState) => void;
  formState?: GeneratorFormState;
  promptInputSuggestions?: PromptInput[][];
  isPremiumGenerator?: boolean | null;
  generatorPlan?: 'freemium' | 'premium' | 'enterprise' | null;
  generatorId?: string;
}) => {
  const { handleInputChange, formState } = useGeneratorFormState(
    promptInputs,
    existingFormState,
  );

  const handleSubmit = useCallback(() => {
    handleConsumeGenerator(formState);
  }, [formState, handleConsumeGenerator]);

  return (
    <GeneratorForm
      handleInputChange={handleInputChange}
      handleSubmit={handleSubmit}
      promptInputSuggestions={promptInputSuggestions}
      promptInputs={promptInputs}
      formState={formState}
      isLoading={isLoading}
      isError={isError}
      isPremiumGenerator={isPremiumGenerator}
      generatorPlan={generatorPlan}
      generatorId={generatorId}
      upgradeLocation="GeneratorPage"
    />
  );
};

const ProfileSectionContainer = styled.div`
  display: flex;
  padding-left: 5px;
  padding-right: 5px;
  box-shadow: none;
  border: none;
  justify-content: flex-start;
  width: 100%;
  align-items: center;
  margin: 20px 0px;
  a {
    &:hover {
      text-decoration: underline;
    }
  }

  h2 {
    display: flex;
    align-items: center;
  }

  span {
    display: flex;
    align-items: baseline;
    padding-right: 6px;
  }

  h2,
  p {
    margin: 0px 0px 0px 20px;
    font-size: 14px;
  }

  p {
    color: ${theme.colors.text.dark};
  }

  @media (max-width: 900px) {
    p {
      font-size: 11px;
    }
  }
`;

export const CoverPhotoRenderer = ({
  id,
  coverImageId,
  coverImageAltText,
}: {
  id: string;
  coverImageId: string;
  coverImageAltText?: string | null;
}) => {
  const { thumbnail, isLoading } = useGeneratorCoverPhoto(id, coverImageId);

  if (!thumbnail && isLoading) {
    return <div style={{ width: '100%', height: '100%', background: 'white' }}></div>;
  }

  return (
    <PhotoImage
      src={thumbnail}
      decoding="async"
      alt={coverImageAltText ? coverImageAltText : undefined}
    />
  );
};

const PhotoImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const NewGeneratorHead = () => {
  const { router } = useRouterMatch();
  const { generator } = useSSRGeneratorNew(router.query.generatorId as string);
  if (!generator) return null;
  return (
    <Head>
      <title>
        {generator.title} by {generator.creatorName} - Curipod
      </title>
      <meta
        property="og:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${generator.title} by ${generator.creatorName} - Curipod`}
      />
      <meta
        name="description"
        content={
          `${generator.description} | A generator using AI to generate pedagogical activities` ||
          ''
        }
        key="namedescription"
      />
      <meta
        property="description"
        content={
          `${generator.description} | A generator using AI to generate pedagogical activities` ||
          ''
        }
        key="description"
      />
      <meta
        property="og:description"
        content={
          `${generator.description} | A generator using AI to generate pedagogical activities` ||
          ''
        }
        key="og:description"
      />
      <meta
        name="twitter:description"
        content={
          `${generator.description} | A generator using AI to generate pedagogical activities` ||
          ''
        }
      />
      <meta
        property="og:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imageurl"
      />
      <meta
        property="og:image:secure_url"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/svg+xml'} key="imagetype" />
      <meta
        property="og:image:alt"
        content={generator.description || ''}
        key="imageAlt"
      />
      <meta property="og:image:width" content={'160'} key="imageWidth" />
      <meta property="og:image:height" content={'108'} key="imageHeight" />
      <meta
        name="twitter:image"
        content={backendService.instance.discover.getGeneratorThumbnailUrl(
          generator.coverImageId || '',
        )}
      />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const GeneratorPage = () => {
  const session = useSession();

  // Designer page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: true,
  });

  // Mobile preview page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`, undefined, {
    priority: true,
  });

  return (
    <>
      <NewAppLayout allowUnAuthenticated={true}>
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <NewGeneratorHead />
          <NewGeneratorPreview authenticated={session.status === 'authenticated'} />
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const queryClient = new QueryClient();
  const session = await getSession({ req: context.req });

  const { generatorId } = context.query as { generatorId: string };
  const flags = await provisionFlags(context, queryClient, session);

  await queryClient.prefetchQuery(
    ['generator-retrieve', generatorId],
    async () => {
      return CoreAPIService.fetchSingleGeneratorElastic(generatorId);
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  return {
    props: {
      ...flags,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      session,
    },
  };
};

export default GeneratorPage;
